package com.fantasytenis.web.screens.bracket

import androidx.compose.runtime.*
import com.fantasytenis.shared.domain.bracket.entity.BracketChallengeEntity
import com.fantasytenis.shared.domain.bracket.entity.BracketChallengeStatus
import com.fantasytenis.shared.domain.bracket.entity.BracketEntryEntity
import com.fantasytenis.shared.domain.bracket.entity.BracketEntryStatus
import com.fantasytenis.shared.core.realtime.ConnectionState
import com.fantasytenis.web.components.AvatarDisplay
import com.fantasytenis.web.components.AvatarSize
import com.fantasytenis.web.components.Button
import com.fantasytenis.web.di.DependencyContainer
import com.fantasytenis.web.hooks.useBracketSubscription
import com.fantasytenis.web.hooks.useRealtime
import com.fantasytenis.web.navigation.NavigationState
import com.fantasytenis.web.navigation.Route
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.borderBottom
import com.fantasytenis.web.theme.h2Style
import com.fantasytenis.web.theme.h3Style
import com.fantasytenis.web.theme.textTransform
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Screen displaying the leaderboard for a bracket challenge.
 */
@Composable
fun BracketLeaderboardScreen(challengeId: String) {
    val scope = rememberCoroutineScope()
    var challenge by remember { mutableStateOf<BracketChallengeEntity?>(null) }
    var entries by remember { mutableStateOf<List<BracketEntryEntity>>(emptyList()) }
    var userEntries by remember { mutableStateOf<List<BracketEntryEntity>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var currentPage by remember { mutableStateOf(0) }
    var lastUpdateTime by remember { mutableStateOf<Long?>(null) }
    val pageSize = 50

    // Initialize realtime connection
    val realtimeState = useRealtime()

    // Subscribe to bracket updates
    useBracketSubscription(
        challengeId = challengeId,
        onLeaderboardUpdate = { update ->
            // Update entries with live data
            val updatedEntries = entries.toMutableList()
            update.entries.forEach { liveEntry ->
                val index = updatedEntries.indexOfFirst { it.id == liveEntry.entryId }
                if (index >= 0) {
                    val existingEntry = updatedEntries[index]
                    updatedEntries[index] = existingEntry.copy(
                        totalPoints = liveEntry.totalPoints,
                        correctPicks = liveEntry.correctPicks,
                        pendingPicks = liveEntry.pendingPicks,
                        possiblePoints = liveEntry.possiblePoints,
                        rank = liveEntry.rank
                    )
                }
            }
            // Re-sort by rank
            entries = updatedEntries.sortedBy { it.rank ?: Int.MAX_VALUE }
            lastUpdateTime = update.timestamp
        }
    )

    // Load data
    LaunchedEffect(challengeId, currentPage) {
        isLoading = true
        error = null

        // Load challenge details
        DependencyContainer.getBracketChallengeUseCase(challengeId).fold(
            onSuccess = { challenge = it },
            onFailure = { error = it.message }
        )

        // Load leaderboard
        DependencyContainer.getBracketLeaderboardUseCase(
            challengeId = challengeId,
            limit = pageSize,
            offset = currentPage * pageSize
        ).fold(
            onSuccess = { entries = it },
            onFailure = { error = it.message }
        )

        // Load user's entries
        DependencyContainer.getUserBracketEntriesUseCase(challengeId).fold(
            onSuccess = { userEntries = it },
            onFailure = { /* User might not have entries */ }
        )

        isLoading = false
    }

    Div({
        style {
            property("height", "var(--app-height)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(Colors.Background)
        }
    }) {
        // Header
        Header({
            style {
                flexShrink(0)
                backgroundColor(Colors.Primary)
                padding(16.px, 24.px)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(16.px)
                }
            }) {
                Button(
                    text = "Back",
                    onClick = {
                        NavigationState.navigate(Route.BracketChallengeDetail(challengeId))
                    },
                    variant = "secondary"
                )
                H2({
                    style {
                        color(Colors.White)
                        fontSize(20.px)
                        fontWeight(700)
                    }
                }) {
                    Text("Leaderboard")
                }
            }

            // Challenge name and live indicator
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(12.px)
                }
            }) {
                challenge?.let {
                    Span({
                        style {
                            color(Color("rgba(255, 255, 255, 0.8)"))
                            fontSize(14.px)
                        }
                    }) {
                        Text(it.name)
                    }
                }

                // Live indicator - only show when challenge is LIVE and connected
                if (realtimeState.isConnected && challenge?.status == BracketChallengeStatus.LIVE) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(6.px)
                            backgroundColor(Color("rgba(76, 175, 80, 0.2)"))
                            padding(4.px, 8.px)
                            borderRadius(12.px)
                        }
                    }) {
                        // Green dot
                        Div({
                            style {
                                width(8.px)
                                height(8.px)
                                borderRadius(50.percent)
                                backgroundColor(Colors.Success)
                            }
                        })
                        Span({
                            style {
                                fontSize(11.px)
                                color(Colors.White)
                                fontWeight(500)
                            }
                        }) {
                            Text("LIVE")
                        }
                    }
                }
            }
        }

        // Main Content - Scrollable area
        Main({
            style {
                flex(1)
                overflowY("auto")
                property("-webkit-overflow-scrolling", "touch")
            }
        }) {
            // Inner container for max-width and padding
            Div({
                style {
                    padding(24.px)
                    maxWidth(900.px)
                    margin(0.px, "auto".unsafeCast<CSSNumeric>())
                }
            }) {
            when {
                isLoading -> LoadingState()
                error != null -> ErrorState(error!!) {
                    scope.launch {
                        isLoading = true
                        DependencyContainer.getBracketLeaderboardUseCase(challengeId).fold(
                            onSuccess = { entries = it; isLoading = false },
                            onFailure = { error = it.message; isLoading = false }
                        )
                    }
                }
                else -> {
                    // User's position highlight
                    if (userEntries.isNotEmpty()) {
                        UserPositionCard(userEntries, challengeId)
                    }

                    // Leaderboard stats
                    challenge?.let { LeaderboardStats(it, entries.size) }

                    // Leaderboard table
                    LeaderboardTable(
                        entries = entries,
                        userEntryIds = userEntries.map { it.id }.toSet(),
                        challengeId = challengeId,
                        startRank = currentPage * pageSize + 1
                    )

                    // Pagination
                    if (entries.size >= pageSize || currentPage > 0) {
                        Pagination(
                            currentPage = currentPage,
                            hasMore = entries.size >= pageSize,
                            onPrevious = { if (currentPage > 0) currentPage-- },
                            onNext = { if (entries.size >= pageSize) currentPage++ }
                        )
                    }
                }
            }
            }
        }
    }
}

@Composable
private fun LoadingState() {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            padding(48.px)
        }
    }) {
        Div({
            style {
                width(40.px)
                height(40.px)
                border(4.px, LineStyle.Solid, Colors.Primary)
                borderRadius(50.percent)
                property("border-top-color", "transparent")
                property("animation", "spin 1s linear infinite")
            }
        })
    }
}

@Composable
private fun ErrorState(message: String, onRetry: () -> Unit) {
    Div({
        style {
            textAlign("center")
            padding(48.px)
        }
    }) {
        P({
            style {
                color(Colors.Error)
                fontSize(16.px)
                marginBottom(16.px)
            }
        }) {
            Text("Error: $message")
        }
        Button(text = "Retry", onClick = onRetry, variant = "primary")
    }
}

@Composable
private fun UserPositionCard(
    userEntries: List<BracketEntryEntity>,
    challengeId: String
) {
    Div({
        style {
            backgroundColor(Colors.Highlight.Background)
            borderRadius(12.px)
            padding(20.px)
            marginBottom(24.px)
        }
    }) {
        H3({
            style {
                fontSize(14.px)
                fontWeight(600)
                color(Colors.Highlight.Text)
                marginBottom(12.px)
            }
        }) {
            Text("Your Brackets")
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(8.px)
            }
        }) {
            userEntries.forEach { entry ->
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                        backgroundColor(Colors.White)
                        padding(12.px, 16.px)
                        borderRadius(8.px)
                    }
                }) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(12.px)
                        }
                    }) {
                        // Rank badge
                        entry.rank?.let { rank ->
                            RankBadge(rank)
                        }

                        // Avatar
                        AvatarDisplay(
                            avatarUrl = entry.avatarUrl,
                            initials = entry.initials,
                            size = AvatarSize.SMALL
                        )

                        Div {
                            Div({
                                style {
                                    fontWeight(500)
                                    fontSize(14.px)
                                }
                            }) {
                                Text(entry.displayName)
                            }
                            Div({
                                style {
                                    fontSize(12.px)
                                    color(Colors.TextSecondary)
                                }
                            }) {
                                Text("${entry.correctPicks} correct • ${entry.pendingPicks} pending")
                            }
                        }
                    }

                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(16.px)
                        }
                    }) {
                        // Points
                        Div({
                            style {
                                textAlign("right")
                            }
                        }) {
                            Div({
                                style {
                                    fontSize(18.px)
                                    fontWeight(600)
                                    color(Colors.Primary)
                                }
                            }) {
                                Text("${entry.totalPoints.toInt()}")
                            }
                            Div({
                                style {
                                    fontSize(11.px)
                                    color(Colors.TextSecondary)
                                }
                            }) {
                                Text("points")
                            }
                        }

                        Button(
                            text = "View",
                            onClick = {
                                NavigationState.navigate(Route.BracketBuilder(challengeId, entry.id))
                            },
                            variant = "secondary"
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun LeaderboardStats(
    challenge: BracketChallengeEntity,
    displayedEntries: Int
) {
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(auto-fit, minmax(120px, 1fr))")
            gap(16.px)
            marginBottom(24.px)
        }
    }) {
        StatBox("Total Entries", "${challenge.currentEntries}")
        StatBox("Prize Pool", challenge.prizePoolDisplay)
        StatBox("Entry Fee", challenge.entryFeeDisplay)
    }
}

@Composable
private fun StatBox(label: String, value: String) {
    Div({
        style {
            backgroundColor(Colors.Surface)
            padding(16.px)
            borderRadius(8.px)
            textAlign("center")
        }
    }) {
        Div({
            style {
                fontSize(11.px)
                color(Colors.TextSecondary)
                marginBottom(4.px)
                textTransform("uppercase")
                letterSpacing(0.5.px)
            }
        }) {
            Text(label)
        }
        Div({
            style {
                fontSize(20.px)
                fontWeight(600)
                color(Colors.TextPrimary)
            }
        }) {
            Text(value)
        }
    }
}

@Composable
private fun LeaderboardTable(
    entries: List<BracketEntryEntity>,
    userEntryIds: Set<String>,
    challengeId: String,
    startRank: Int
) {
    Div({
        style {
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            overflow("hidden")
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
        }
    }) {
        // Table header
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "60px 1fr 80px 80px 100px")
                padding(12.px, 16.px)
                backgroundColor(Colors.SurfaceVariant)
                borderBottom(1.px, LineStyle.Solid, Colors.Border)
                fontSize(12.px)
                fontWeight(600)
                color(Colors.TextSecondary)
                textTransform("uppercase")
                letterSpacing(0.5.px)
            }
        }) {
            Span { Text("Rank") }
            Span { Text("Player") }
            Span({
                style { textAlign("center") }
            }) { Text("Correct") }
            Span({
                style { textAlign("center") }
            }) { Text("Pending") }
            Span({
                style { textAlign("right") }
            }) { Text("Points") }
        }

        // Table rows
        if (entries.isEmpty()) {
            Div({
                style {
                    padding(32.px)
                    textAlign("center")
                    color(Colors.TextSecondary)
                }
            }) {
                Text("No entries yet")
            }
        } else {
            entries.forEachIndexed { index, entry ->
                val isUserEntry = entry.id in userEntryIds
                val displayRank = entry.rank ?: (startRank + index)

                Div({
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", "60px 1fr 80px 80px 100px")
                        padding(12.px, 16.px)
                        alignItems(AlignItems.Center)
                        backgroundColor(if (isUserEntry) Colors.Highlight.Background else Colors.White)
                        borderBottom(1.px, LineStyle.Solid, Colors.BorderLight)
                        cursor("pointer")
                        property("transition", "background-color 0.2s")
                    }
                    onClick {
                        NavigationState.navigate(Route.BracketBuilder(challengeId, entry.id))
                    }
                }) {
                    // Rank
                    RankBadge(displayRank)

                    // Player with avatar
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(12.px)
                        }
                    }) {
                        // Avatar
                        AvatarDisplay(
                            avatarUrl = entry.avatarUrl,
                            initials = entry.initials,
                            size = AvatarSize.SMALL
                        )

                        // Name and status
                        Div {
                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    gap(6.px)
                                }
                            }) {
                                Span({
                                    style {
                                        fontWeight(if (isUserEntry) 600 else 500)
                                        fontSize(14.px)
                                        color(Colors.TextPrimary)
                                    }
                                }) {
                                    Text(entry.displayName)
                                }
                                if (isUserEntry) {
                                    Span({
                                        style {
                                            fontSize(11.px)
                                            color(Colors.Highlight.Text)
                                            fontWeight(500)
                                        }
                                    }) {
                                        Text("(You)")
                                    }
                                }
                            }
                            // Status indicator
                            if (entry.status == BracketEntryStatus.PAID) {
                                Div({
                                    style {
                                        fontSize(11.px)
                                        color(Colors.SuccessDark)
                                        fontWeight(500)
                                    }
                                }) {
                                    Text("Won $${entry.payoutAmount / 100.0}!")
                                }
                            }
                        }
                    }

                    // Correct picks
                    Div({
                        style {
                            textAlign("center")
                            fontSize(14.px)
                            color(Colors.SuccessDark)
                            fontWeight(500)
                        }
                    }) {
                        Text("${entry.correctPicks}")
                    }

                    // Pending picks
                    Div({
                        style {
                            textAlign("center")
                            fontSize(14.px)
                            color(if (entry.pendingPicks > 0) Colors.WarningDark else Colors.TextSecondary)
                        }
                    }) {
                        Text("${entry.pendingPicks}")
                    }

                    // Points
                    Div({
                        style {
                            textAlign("right")
                        }
                    }) {
                        Div({
                            style {
                                fontSize(16.px)
                                fontWeight(600)
                                color(Colors.Primary)
                            }
                        }) {
                            Text("${entry.totalPoints.toInt()}")
                        }
                        if (entry.possiblePoints > entry.totalPoints) {
                            Div({
                                style {
                                    fontSize(11.px)
                                    color(Colors.TextSecondary)
                                }
                            }) {
                                Text("(${entry.possiblePoints.toInt()} max)")
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun RankBadge(rank: Int) {
    val (bgColor, textColor) = when (rank) {
        1 -> Colors.Medal.GoldBackground to Colors.Medal.GoldText
        2 -> Colors.Medal.SilverBackground to Colors.Medal.SilverText
        3 -> Colors.Medal.BronzeBackground to Colors.Medal.BronzeText
        else -> Colors.SurfaceVariant to Colors.TextPrimary
    }

    Div({
        style {
            width(36.px)
            height(36.px)
            borderRadius(50.percent)
            backgroundColor(bgColor)
            color(textColor)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            fontSize(14.px)
            fontWeight(600)
        }
    }) {
        Text("$rank")
    }
}

@Composable
private fun Pagination(
    currentPage: Int,
    hasMore: Boolean,
    onPrevious: () -> Unit,
    onNext: () -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            gap(16.px)
            marginTop(24.px)
        }
    }) {
        Button(
            text = "Previous",
            onClick = onPrevious,
            variant = if (currentPage > 0) "secondary" else "disabled"
        )

        Span({
            style {
                fontSize(14.px)
                color(Colors.TextSecondary)
            }
        }) {
            Text("Page ${currentPage + 1}")
        }

        Button(
            text = "Next",
            onClick = onNext,
            variant = if (hasMore) "secondary" else "disabled"
        )
    }
}
